// src/components/Usuarios.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Usuarios.css';
import { useAuth } from '../hooks/useAuth';
import {
  FaKey, FaUserCheck, FaUserSlash, FaUserPlus,
  FaEdit, FaSignOutAlt, FaLock, FaUnlock, FaTrash,
  FaArrowLeft, FaShieldAlt, FaGoogle, FaMicrosoft,
  FaFingerprint, FaHistory, FaExclamationTriangle,
  FaTimes, FaBroom, FaIdCard, FaUserShield,
  FaBuilding, FaFilter,
} from 'react-icons/fa';
import { toast } from 'react-toastify';

// Modais de governança
import { SimpleEditModal, EditModalAdminGeral } from './UsuariosPermissoes';
import './UsuariosPermissoes.css';

// Modal de histórico + PostHog
import { UserHistoryModal } from './UserHistoryModal';

// Cadastro de novos colaboradores
import NovosUsuarios from '../components/NovosUsuarios';

/* ========================================================================
 * Constantes de API
 * ====================================================================== */
const API_USERS_LIST           = '/api/usuarios';
const API_BULK_ACTIONS         = '/api/usuarios/bulk-actions';
const API_CLIENTS_LIST         = '/api/clientes-sistema/list';
const API_LICENSING_STATUS     = '/api/licensing/status';
const API_LICENSING_ALLOCATE   = '/api/licensing/allocate';
const API_LICENSING_DEALLOCATE = '/api/licensing/deallocate';
const API_KEYS_BY_CLIENT       = (clientId) => `/api/usuarios/licencas/cliente/${clientId}`;
const API_DELETED_USERS        = '/api/usuarios/excluidos';
const API_RESTORE_USER         = (id)       => `/api/usuarios/${id}/restore`;
const API_PURGE_USER           = (id)       => `/api/usuarios/${id}/hard`;
const API_PING                 = '/api/usuarios/ping';

/* ========================================================================
 * Sanitização
 * ====================================================================== */
const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const safeString = (v, fallback = '') => {
  if (v === null || v === undefined) return fallback;
  return String(v).replace(/[\u0000-\u001F\u007F]/g, '').trim();
};

const safeLower  = (v, fallback = '') => safeString(v, fallback).toLowerCase();
const safeInt    = (v, fallback = 0)  => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : fallback; };
const safeBool   = (v, fallback = false) => {
  if (typeof v === 'boolean') return v;
  if (v === 1 || v === '1' || v === 'true')  return true;
  if (v === 0 || v === '0' || v === 'false') return false;
  return fallback;
};

const safeDate = (v) => { const d = new Date(v); return Number.isFinite(d.getTime()) ? d : null; };

const safeHttpUrl = (value) => {
  const v = safeString(value, '');
  if (!v) return '';
  if (v.startsWith('data:image/')) return v;
  try {
    const u = new URL(v);
    return (u.protocol === 'http:' || u.protocol === 'https:') ? u.toString() : '';
  } catch { return ''; }
};

const safeAvatarUrl = (name, foto_perfil) => {
  const safeFoto = safeHttpUrl(foto_perfil);
  if (safeFoto) return safeFoto;
  const nm = safeString(name, 'Usuário');
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(nm)}&background=101C5D&color=fff`;
};

const sanitizeUser = (raw) => {
  const u  = isObject(raw) ? raw : {};
  const id = safeInt(u.id, 0);
  return {
    id,
    nome:                safeString(u.nome, 'Usuário'),
    email:               safeLower(u.email, 'sem-email@local'),
    perfil:              safeString(u.perfil, 'operacional'),
    status:              safeString(u.status, 'ativo') === 'bloqueado' ? 'bloqueado' : 'ativo',
    cliente_id:          u.cliente_id !== undefined ? safeString(u.cliente_id, '') : '',
    // nome do tenant pode vir direto do backend; fallback calculado no front
    cliente_nome:        safeString(u.cliente_nome || u.empresa_nome || '', ''),
    licenca_chave_id:    u.licenca_chave_id ? safeString(u.licenca_chave_id, '') : '',
    foto_perfil:         safeHttpUrl(u.foto_perfil),
    sso_google:          safeBool(u.sso_google, false),
    sso_microsoft:       safeBool(u.sso_microsoft, false),
    login_local_enabled: u.login_local_enabled === undefined ? true : !!safeInt(u.login_local_enabled, 1),
    last_login_at:       u.ultimo_acesso || u.last_login_at || null,
    last_logout_at:      u.last_logout_at || null,
    deleted_at:          u.deleted_at || null,
  };
};

const sanitizeUsersArray = (payload) => {
  const arr     = Array.isArray(payload) ? payload : [];
  const cleaned = arr.map(sanitizeUser).filter(u => u.id > 0);
  const map     = new Map();
  cleaned.forEach(u => map.set(u.id, u));
  return Array.from(map.values());
};

const sanitizeQuery = (v) => { const s = safeString(v, ''); return s.length > 80 ? s.slice(0, 80) : s; };

/* ========================================================================
 * Presença (online/offline)
 * ====================================================================== */
const ONLINE_WINDOW_MINUTES = 2;

const isOnline = (user, nowTs, windowMinutes = ONLINE_WINDOW_MINUTES) => {
  const last   = safeDate(user?.last_login_at);
  if (!last) return false;
  const logout = safeDate(user?.last_logout_at);
  if (logout && logout.getTime() >= last.getTime()) return false;
  const diffMs = nowTs - last.getTime();
  return diffMs >= 0 && diffMs <= windowMinutes * 60 * 1000;
};

/* ========================================================================
 * ConfirmModal
 * ====================================================================== */
function ConfirmModal({ open, title, message, confirmText = 'Confirmar', cancelText = 'Cancelar', variant = 'danger', loading = false, onConfirm, onClose }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={title || 'Confirmar ação'}>
      <div className="modal-content modal-content--simple">
        <header className="modal-header-simple">
          <h3 style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {variant === 'danger' ? <FaExclamationTriangle /> : <FaUserShield />}
            {title || 'Confirmar ação'}
          </h3>
          <button onClick={onClose} className="modal-close-button" title="Fechar"><FaTimes /></button>
        </header>
        <div className="modal-body-simple">
          <p style={{ color: '#334155' }}>{message}</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn-ultra secondary" onClick={onClose} disabled={loading}>{cancelText}</button>
            <button
              className={`btn-ultra ${variant === 'danger' ? 'primary' : 'dark'}`}
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? 'Processando...' : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========================================================================
 * Subcomponentes
 * ====================================================================== */
const LicenseStatus = ({ usuario }) => {
  if (usuario.perfil === 'admin_geral') {
    return (
      <div className="license-status-cell native" title="Usuário nativo do sistema.">
        <FaUnlock /> <span>Acesso Nativo</span>
      </div>
    );
  }
  if (usuario.licenca_chave_id) {
    return (
      <div className="license-status-cell allocated" title="Este usuário possui uma licença ativa.">
        <FaUserCheck /> <span>Licenciado</span>
      </div>
    );
  }
  return (
    <div className="license-status-cell not-allocated" title="Usuário aguardando alocação de licença.">
      <FaUserSlash /> <span>Não Licenciado</span>
    </div>
  );
};

const AuthProviderBadge = ({ usuario }) => {
  const hasGoogle    = !!usuario.sso_google;
  const hasMicrosoft = !!usuario.sso_microsoft;
  return (
    <div className="auth-provider-badges">
      {hasGoogle && (
        <span className="tag-native tag-sso" title="SSO Google">
          <FaGoogle size={10} /> Google
        </span>
      )}
      {hasMicrosoft && (
        <span className="tag-native tag-sso" title="SSO Microsoft">
          <FaMicrosoft size={10} /> Microsoft
        </span>
      )}
      {!hasGoogle && !hasMicrosoft && (
        <span className="tag-native tag-sso" title="Login local">
          <FaFingerprint size={10} /> Local
        </span>
      )}
      {(hasGoogle || hasMicrosoft) && !usuario.login_local_enabled && (
        <span className="tag-native tag-sso-only" title="Acesso somente via SSO">
          <FaLock size={9} /> SSO-only
        </span>
      )}
    </div>
  );
};

const BulkActionsBar = ({ users, selectedIds, onForceLogout, onBlock, onUnblock, onDelete, onClear, onMfaAction }) => {
  if (!selectedIds || selectedIds.length === 0) return null;

  const selectedUsers = users.filter(u => selectedIds.includes(u.id));
  const allBlocked    = selectedUsers.length > 0 && selectedUsers.every(u => u.status === 'bloqueado');
  const allActive     = selectedUsers.length > 0 && selectedUsers.every(u => u.status === 'ativo');
  const mixed         = !allBlocked && !allActive;

  return (
    <div className="bulk-toolbar">
      <div className="bulk-toolbar__left">
        <span className="bulk-toolbar__count">{selectedIds.length} selecionado{selectedIds.length > 1 ? 's' : ''}</span>
      </div>
      <div className="bulk-toolbar__right">
        <button className="bulk-btn info"   onClick={onMfaAction}   title="Configurar MFA"><FaShieldAlt /> MFA em Massa</button>
        <button className="bulk-btn warn"   onClick={onForceLogout} title="Derrubar Sessão"><FaSignOutAlt /> Derrubar Sessão</button>
        {allActive  && <button className="bulk-btn info"   onClick={onBlock}   title="Bloquear"><FaLock />   Bloquear</button>}
        {allBlocked && <button className="bulk-btn info"   onClick={onUnblock} title="Desbloquear"><FaUnlock /> Desbloquear</button>}
        {mixed && <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>(seleção mista — ação restrita)</span>}
        <button className="bulk-btn danger" onClick={onDelete}  title="Excluir"><FaTrash />   Excluir</button>
        <button className="bulk-btn ghost"  onClick={onClear}   title="Limpar seleção"><FaBroom /> Limpar</button>
      </div>
    </div>
  );
};

const LicenseManagementTab = ({ keys, users, onAllocate, onDeallocate }) => {
  const unassignedUsers = useMemo(
    () => users.filter(u => !u.licenca_chave_id && u.perfil !== 'admin_geral'),
    [users],
  );
  const [selectedUsersMap, setSelectedUsersMap] = useState({});

  const handleAllocateClick = (keyId) => {
    const userIdToAssign = selectedUsersMap[keyId];
    if (!userIdToAssign) { toast.warn('Selecione um colaborador para ocupar esta licença.'); return; }
    onAllocate(userIdToAssign, keyId);
    setSelectedUsersMap(prev => { const clone = { ...prev }; delete clone[keyId]; return clone; });
  };

  return (
    <div className="license-management-tab">
      {keys.length === 0 ? (
        <div className="empty-state">
          <h4>Nenhuma licença encontrada para este cliente.</h4>
          <p>Quando houver chaves disponíveis, você poderá vinculá-las aqui.</p>
        </div>
      ) : (
        <div className="license-grid-container">
          {keys.map(key => (
            <div key={key.id} className={`license-card ${key.usuario_id_alocado ? 'allocated' : 'available'}`}>
              <div className="license-card-header">
                <div className="license-icon-box"><FaKey /></div>
                <div className="license-title-box">
                  <h4>Licença #{String(key.id).padStart(6, '0')}</h4>
                  <span className="license-status-text">{key.usuario_id_alocado ? 'Em Uso' : 'Disponível'}</span>
                </div>
              </div>
              <div className="license-card-body">
                {key.usuario_id_alocado ? (
                  <div className="user-info-allocated">
                    <img src={safeAvatarUrl(key.usuario_nome, key.usuario_foto)} alt={safeString(key.usuario_nome, 'Usuário')} />
                    <div className="user-details">
                      <strong>{safeString(key.usuario_nome, 'Usuário')}</strong>
                      {key.data_alocacao && <span>Desde: {new Date(key.data_alocacao).toLocaleDateString('pt-BR')}</span>}
                    </div>
                  </div>
                ) : (
                  <div className="license-available-info">
                    <p>Vincular colaborador:</p>
                    <div className="allocation-form">
                      <select
                        value={selectedUsersMap[key.id] || ''}
                        onChange={e => setSelectedUsersMap(prev => ({ ...prev, [key.id]: e.target.value }))}
                      >
                        <option value="">Selecione...</option>
                        {unassignedUsers.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <div className="license-card-footer">
                {key.usuario_id_alocado ? (
                  <button className="deallocate-btn" onClick={() => onDeallocate(key.id)}>Liberar Vaga</button>
                ) : (
                  <button
                    className="allocate-btn"
                    onClick={() => handleAllocateClick(key.id)}
                    disabled={unassignedUsers.length === 0 || !selectedUsersMap[key.id]}
                  >
                    Vincular
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── UserRow ──────────────────────────────────────────────────────────────────
// clientMap: Map<string, string> — cliente_id → nome_empresa
// showTenant: boolean — exibe o badge de tenant (somente para admin_geral)
const UserRow = ({
  u, now, isSelected, onToggleSelect, isNative,
  onGovern, onToggleBlock, onDelete, onAudit,
  perfilLogado, clientMap, showTenant,
}) => {
  const isAdminGeral = safeLower(perfilLogado, '').trim() === 'admin_geral';

  // Resolve nome do tenant: prioridade para campo já populado pelo backend,
  // senão faz lookup no clientMap passado pelo componente pai.
  const tenantNome = useMemo(() => {
    if (!showTenant || !u.cliente_id) return null;
    if (u.cliente_nome) return u.cliente_nome;
    return clientMap?.get(safeString(u.cliente_id)) || null;
  }, [showTenant, u.cliente_id, u.cliente_nome, clientMap]);

  return (
    <li className={`user-row-item ${u.status === 'bloqueado' ? 'is-blocked' : ''} ${isNative ? 'is-native' : ''}`}>
      <div className="cell-check">
        {!isNative ? (
          <input aria-label={`Selecionar ${u.nome}`} type="checkbox" checked={isSelected} onChange={onToggleSelect} />
        ) : (
          <FaLock className="icon-native" title="Usuário Protegido (Nativo)" />
        )}
      </div>

      <div className="cell-main">
        <div className="avatar-wrapper">
          <img src={safeAvatarUrl(u.nome, u.foto_perfil)} alt={`Avatar de ${u.nome}`} />
          {isNative && <div className="native-badge-icon" title="Admin Geral"><FaShieldAlt /></div>}
        </div>
        <div className="user-meta">
          <strong style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span
              className={`presence-dot ${isOnline(u, now, ONLINE_WINDOW_MINUTES) ? 'online' : 'offline'}`}
              title={isOnline(u, now, ONLINE_WINDOW_MINUTES) ? 'Online' : 'Offline'}
              aria-label={isOnline(u, now, ONLINE_WINDOW_MINUTES) ? 'Online' : 'Offline'}
            />
            {u.nome}
            {isNative && <span className="tag-native">Sistema</span>}
          </strong>
          <span>{u.email}</span>
        </div>
      </div>

      {/* ── Coluna independente: Vínculo & Acesso ── */}
      <div className="cell-attrs">
        {tenantNome ? (
          <span className="tag-tenant" title={`Tenant: ${tenantNome}`}>
            <FaBuilding size={9} />
            {tenantNome}
          </span>
        ) : (showTenant && <span className="tag-tenant tag-tenant--empty" title="Sem tenant vinculado"><FaBuilding size={9} /> Sem tenant</span>)}
        <AuthProviderBadge usuario={u} />
      </div>

      <div className="cell-status"><LicenseStatus usuario={u} /></div>

      <div className="cell-actions" style={{ pointerEvents: 'auto' }}>
        <button onClick={onGovern} className="action-btn edit" title="Governança" style={{ pointerEvents: 'auto' }}>
          <FaEdit /> Governança
        </button>

        <button onClick={onAudit} className="action-btn edit" title="Histórico" style={{ pointerEvents: 'auto' }}>
          <FaHistory /> Histórico
        </button>

        <button
          onClick={onToggleBlock}
          className={`action-btn block ${isNative ? 'disabled' : ''}`}
          disabled={isNative}
          title={isNative ? 'Ação bloqueada para Admin Nativo' : (u.status === 'ativo' ? 'Bloquear' : 'Desbloquear')}
          style={{ pointerEvents: isNative ? 'none' : 'auto' }}
        >
          {u.status === 'ativo' ? <FaLock /> : <FaUnlock />}
        </button>

        {isAdminGeral && (
          <button
            onClick={onDelete}
            className={`action-btn delete ${isNative ? 'disabled' : ''}`}
            disabled={isNative}
            title={isNative ? 'Ação bloqueada para Admin Nativo' : 'Excluir Usuário'}
            style={{ pointerEvents: isNative ? 'none' : 'auto' }}
          >
            <FaTrash /> Excluir
          </button>
        )}
      </div>
    </li>
  );
};

const UsersTable = ({
  users, now, selectedIds, toggleSelectOne, toggleSelectAll,
  canWrite, perfilLogado, onGovern, onToggleBlock, onDelete, onAudit,
  clientMap, showTenant,
}) => {
  const protectedIds = useMemo(
    () => new Set(users.filter(u => u.perfil === 'admin_geral' || u.email === 'admin@guinchooliveira.com').map(u => u.id)),
    [users],
  );
  const selectableIds = useMemo(() => users.map(u => u.id).filter(id => !protectedIds.has(id)), [users, protectedIds]);
  const allSelected   = selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id));
  const someSelected  = selectableIds.some(id => selectedIds.includes(id)) && !allSelected;

  return (
    <ul className="user-list user-list--dir">
      <li className="list-header-row">
        <div className="cell-check">
          {canWrite ? (
            <input
              type="checkbox"
              aria-label="Selecionar todos"
              checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected; }}
              onChange={toggleSelectAll}
            />
          ) : <span />}
        </div>
        <div className="cell-main">Usuário</div>
        <div className="cell-attrs">{showTenant ? 'Vínculo & Acesso' : 'Acesso'}</div>
        <div className="cell-status">Licença</div>
        <div className="cell-actions">Ações</div>
      </li>

      {users.length === 0 ? (
        <div className="empty-state">
          <h4>Nenhum usuário encontrado</h4>
          <p>Ajuste o filtro ou cadastre um novo colaborador.</p>
        </div>
      ) : users.map(u => {
        const isNative   = u.perfil === 'admin_geral' || u.email === 'admin@guinchooliveira.com';
        const isSelected = selectedIds.includes(u.id);
        return (
          <UserRow
            key={u.id}
            u={u}
            now={now}
            isSelected={isSelected}
            onToggleSelect={() => toggleSelectOne(u.id)}
            isNative={isNative}
            onGovern={() => onGovern(u)}
            onToggleBlock={() => onToggleBlock(u)}
            onDelete={() => onDelete(u.id)}
            onAudit={() => onAudit(u)}
            perfilLogado={perfilLogado}
            clientMap={clientMap}
            showTenant={showTenant}
          />
        );
      })}
    </ul>
  );
};

const Pagination = ({ page, totalPages, onPage }) => {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, start + 4);
  const pages = [];
  for (let p = start; p <= end; p++) pages.push(p);
  return (
    <div className="pagination">
      <button className="pg-btn" disabled={page === 1} onClick={() => onPage(page - 1)}>Anterior</button>
      {pages.map(p => (
        <button key={p} className={`pg-btn ${p === page ? 'active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="pg-btn" disabled={page === totalPages} onClick={() => onPage(page + 1)}>Próxima</button>
      <span className="pg-info">Página {page} de {totalPages}</span>
    </div>
  );
};

/* ======================================================================
 * LicenseRequestModal
 * ====================================================================== */
function LicenseRequestModal({ currentLicenses, clientName, onClose, onSubmit }) {
  const [requestedAmount, setRequestedAmount] = useState(5);
  const [justification, setJustification]     = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(requestedAmount, justification);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-content--simple">
        <header className="modal-header-simple">
          <h3>Solicitar Licenças Adicionais</h3>
          <button onClick={onClose} className="modal-close-button" title="Fechar"><FaTimes /></button>
        </header>
        <div className="modal-body-simple">
          <p>Cliente: <strong>{safeString(clientName, '-')}</strong></p>
          <p>Capacidade Atual: <strong>{safeInt(currentLicenses, 0)} usuários</strong></p>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Quantidade desejada:</label>
              <input
                type="number" min="1" value={requestedAmount} required
                onChange={(e) => setRequestedAmount(safeInt(e.target.value, 1))}
              />
            </div>
            <div className="form-group">
              <label>Justificativa do upgrade:</label>
              <textarea
                rows="3" value={justification}
                onChange={(e) => setJustification(safeString(e.target.value, ''))}
                placeholder="Descreva o motivo da solicitação..."
              />
            </div>
            <button type="submit" className="submit-button">Enviar Pedido de Compra</button>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ======================================================================
 * Componente Principal
 * ====================================================================== */
export default function Usuarios() {
  const navigate          = useNavigate();
  const { perfil: perfilLogado } = useAuth() || {};

  // Re-render periódico para bolinha de presença
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  /* ------------------------------------------------------------------
   * AbortController — cancela requests de listagem em voo ao trocar
   * filtros ou ao desmontar o componente.
   * ------------------------------------------------------------------ */
  const abortUsersRef   = useRef(null);
  const abortDeletedRef = useRef(null);

  useEffect(() => {
    return () => {
      abortUsersRef.current?.abort();
      abortDeletedRef.current?.abort();
    };
  }, []);

  /* ------------------------------------------------------------------
   * Resolução de perfil (hook + JWT fallback + /me)
   * ------------------------------------------------------------------ */
  const normalizeRole = (value) => {
    const k = safeString(value, '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^\w]+/g, '_')
      .trim();

    const adminAliases      = new Set(['admin', 'administrator', 'administrador']);
    const adminGeralAliases = new Set(['admin_geral', 'superadmin', 'super_admin', 'root']);
    if (adminAliases.has(k))      return 'admin';
    if (adminGeralAliases.has(k)) return 'admin_geral';
    return k;
  };

  const decodeRoleFromJwt = (tok) => {
    try {
      if (!tok || tok.split('.').length !== 3) return '';
      const [, payloadB64] = tok.split('.');
      const json = JSON.parse(atob(payloadB64));
      return normalizeRole(json?.perfil || json?.roleKey || '');
    } catch { return ''; }
  };

  const getTokenFromAxiosHeader = () => {
    try {
      const hdr = api?.defaults?.headers?.common?.Authorization || api?.defaults?.headers?.Authorization || '';
      const m   = typeof hdr === 'string' ? hdr.match(/Bearer\s+(.+)/i) : null;
      return m ? m[1] : '';
    } catch { return ''; }
  };

  const getRoleFromJwtAnywhere = () => {
    let tok  = localStorage.getItem('token') || localStorage.getItem('authToken') ||
               localStorage.getItem('jwt')   || localStorage.getItem('access_token');
    let role = decodeRoleFromJwt(tok);
    if (role) return role;
    tok  = sessionStorage.getItem('token') || sessionStorage.getItem('authToken') ||
           sessionStorage.getItem('jwt')   || sessionStorage.getItem('access_token');
    role = decodeRoleFromJwt(tok);
    if (role) return role;
    try {
      const m = document.cookie.match(/(?:^|;\s*)(token|authToken|jwt|access_token)=([^;]+)/);
      if (m) { role = decodeRoleFromJwt(decodeURIComponent(m[2])); if (role) return role; }
    } catch {}
    role = decodeRoleFromJwt(getTokenFromAxiosHeader());
    return role || '';
  };

  const [perfilKey, setPerfilKey] = useState(() => {
    const fromHook = normalizeRole(perfilLogado);
    return fromHook || getRoleFromJwtAnywhere() || '';
  });

  useEffect(() => {
    const fromHook = normalizeRole(perfilLogado);
    if (fromHook && fromHook !== perfilKey) setPerfilKey(fromHook);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfilLogado]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (perfilKey) return;
      try {
        const { data } = await api.get('/api/usuarios/me');
        const role = normalizeRole(data?.perfil);
        if (!cancelled && role) setPerfilKey(role);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [perfilKey]);

  const isAdminGeral = perfilKey === 'admin_geral';
  const isAdmin      = perfilKey === 'admin';
  const canWrite     = isAdminGeral || isAdmin;

  /* ------------------------------------------------------------------
   * Estados principais
   * ------------------------------------------------------------------ */
  const [usuariosRaw, setUsuariosRaw] = useState([]);
  const usuarios = useMemo(() => sanitizeUsersArray(usuariosRaw), [usuariosRaw]);

  const [searchQuery, setSearchQuery]               = useState('');
  const [activeTab, setActiveTab]                   = useState('usuarios');
  const [allClients, setAllClients]                 = useState([]);
  const allClientsLoadedRef                         = useRef(false);
  const [licenseInfo, setLicenseInfo]               = useState({ used: 0, total: 0, clientName: '' });
  const [keysOverview, setKeysOverview]             = useState([]);

  // ── FILTRO DE TENANT (admin_geral) ─────────────────────────────────────────
  // '' = todos os tenants | '<id>' = tenant específico
  const [selectedTenantFilter, setSelectedTenantFilter] = useState('');

  // Paginação
  const PAGE_SIZE = 10;
  const [page, setPage]             = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((totalUsers || usuarios.length) / PAGE_SIZE)),
    [totalUsers, usuarios.length],
  );

  // Excluídos
  const [deletedRaw, setDeletedRaw]     = useState([]);
  const deletedUsers = useMemo(() => sanitizeUsersArray(deletedRaw), [deletedRaw]);
  const [deletedPage, setDeletedPage]   = useState(1);
  const [deletedTotal, setDeletedTotal] = useState(0);
  const deletedTotalPages = useMemo(
    () => Math.max(1, Math.ceil((deletedTotal || deletedUsers.length) / PAGE_SIZE)),
    [deletedTotal, deletedUsers.length],
  );
  const [deletedQuery, setDeletedQuery] = useState('');

  // Modais
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen]       = useState(false);
  const [editingUser, setEditingUser]               = useState(null);
  const [isAddModalOpen, setIsAddModalOpen]         = useState(false);
  const [mfaBulkModalOpen, setMfaBulkModalOpen]     = useState(false);

  // Confirm modal
  const [confirmState, setConfirmState] = useState({
    open: false, title: '', message: '',
    confirmText: 'Confirmar', cancelText: 'Cancelar',
    variant: 'danger', loading: false, onConfirm: null,
  });

  // Histórico
  const [historyUserId, setHistoryUserId] = useState(null);

  // Seleções
  const [selectedClientForKeys, setSelectedClientForKeys] = useState('');
  const [selectedUsers, setSelectedUsers]                 = useState([]);

  const limitReached = licenseInfo.total > 0 && licenseInfo.used >= licenseInfo.total;

  // ── Mapa de tenant: O(1) lookup por cliente_id ─────────────────────────────
  const clientMap = useMemo(() => {
    const m = new Map();
    allClients.forEach(c => m.set(safeString(c.id), safeString(c.nome_empresa, '?')));
    return m;
  }, [allClients]);

  // Exibe badge de tenant quando admin_geral está sem filtro (visão consolidada)
  // OU quando está filtrado (reforço visual de contexto)
  const showTenantBadge = isAdminGeral;

  // Resets ao mudar filtros / aba
  useEffect(() => { setPage(1); }, [searchQuery]);
  useEffect(() => { setPage(1); }, [selectedTenantFilter]);
  useEffect(() => { setDeletedPage(1); },    [deletedQuery]);
  useEffect(() => { setSelectedUsers([]); }, [activeTab]);
  useEffect(() => { setSelectedUsers([]); }, [page]);

  /* ------------------------------------------------------------------
   * Heartbeat
   * ------------------------------------------------------------------ */
  useEffect(() => {
    let timer = null;
    const sendPing = async () => { try { await api.post(API_PING); } catch {} };
    const start = () => { if (timer) return; sendPing(); timer = setInterval(sendPing, 60 * 1000); };
    const stop  = () => { if (timer) clearInterval(timer); timer = null; };
    const handleVisibility = () => { if (document.visibilityState === 'visible') start(); else stop(); };

    handleVisibility();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', start);
    window.addEventListener('blur',  stop);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', start);
      window.removeEventListener('blur',  stop);
    };
  }, []);

  /* ------------------------------------------------------------------
   * Fetchers
   * ------------------------------------------------------------------ */
  const fetchClientsIfNeeded = useCallback(async () => {
    if (!canWrite) return;
    if (allClientsLoadedRef.current && allClients.length > 0) return;
    try {
      const { data } = await api.get(API_CLIENTS_LIST);
      setAllClients(Array.isArray(data) ? data : []);
      allClientsLoadedRef.current = true;
    } catch { setAllClients([]); }
  }, [canWrite, allClients.length]);

  const fetchLicenseStatusIfAdmin = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get(API_LICENSING_STATUS);
      const payload  = isObject(data) ? data : {};
      setLicenseInfo({
        used: safeInt(payload.used, 0),
        total: safeInt(payload.total, 0),
        clientName: safeString(payload.clientName, ''),
      });
    } catch { setLicenseInfo({ used: 0, total: 0, clientName: '' }); }
  }, [isAdmin]);

  const fetchUsers = useCallback(async (query = '', pageToFetch = page) => {
    // Cancela request anterior em voo
    abortUsersRef.current?.abort();
    const controller = new AbortController();
    abortUsersRef.current = controller;

    try {
      const q      = sanitizeQuery(query);
      const params = { query: q, page: pageToFetch, limit: PAGE_SIZE };

      // Filtro de tenant — somente para admin_geral
      if (isAdminGeral && selectedTenantFilter) {
        params.cliente_id = selectedTenantFilter;
      }

      const { data: payload } = await api.get(API_USERS_LIST, {
        params,
        signal: controller.signal,
      });

      if (isObject(payload) && !Array.isArray(payload)) {
        const items = payload.items || payload.data || payload.users || [];
        const total = payload.total ?? payload.totalResults ?? payload.count ?? (Array.isArray(items) ? items.length : 0);
        setUsuariosRaw(Array.isArray(items) ? items : []);
        setTotalUsers(safeInt(total, 0));
      } else {
        const arr   = Array.isArray(payload) ? payload : [];
        const start = (pageToFetch - 1) * PAGE_SIZE;
        setUsuariosRaw(arr.slice(start, start + PAGE_SIZE));
        setTotalUsers(arr.length);
      }

      await fetchClientsIfNeeded();
      await fetchLicenseStatusIfAdmin();
    } catch (error) {
      // Ignora erros de request cancelado (troca de filtro, desmontagem)
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      if (error?.response?.status !== 403) toast.error('Falha na comunicação com o servidor.');
    }
  }, [page, isAdminGeral, selectedTenantFilter, fetchClientsIfNeeded, fetchLicenseStatusIfAdmin]);

  const fetchDeletedUsers = useCallback(async (query = '', pageToFetch = deletedPage) => {
    if (!isAdminGeral) return;

    abortDeletedRef.current?.abort();
    const controller = new AbortController();
    abortDeletedRef.current = controller;

    try {
      const q = sanitizeQuery(query);
      const { data: payload } = await api.get(API_DELETED_USERS, {
        params: { query: q, page: pageToFetch, limit: PAGE_SIZE },
        signal: controller.signal,
      });

      if (isObject(payload) && !Array.isArray(payload)) {
        const items = payload.items || payload.data || payload.users || [];
        const total = payload.total ?? payload.totalResults ?? payload.count ?? (Array.isArray(items) ? items.length : 0);
        setDeletedRaw(Array.isArray(items) ? items : []);
        setDeletedTotal(safeInt(total, 0));
      } else {
        const arr   = Array.isArray(payload) ? payload : [];
        const start = (pageToFetch - 1) * PAGE_SIZE;
        setDeletedRaw(arr.slice(start, start + PAGE_SIZE));
        setDeletedTotal(arr.length);
      }
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      setDeletedRaw([]);
      setDeletedTotal(0);
      toast.info('Aba "Usuários Excluídos" indisponível (endpoint não encontrado).');
    }
  }, [deletedPage, isAdminGeral]);

  useEffect(() => {
    if (activeTab === 'usuarios') fetchUsers(searchQuery, page);
  }, [activeTab, searchQuery, page, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'excluidos' && isAdminGeral) fetchDeletedUsers(deletedQuery, deletedPage);
  }, [activeTab, isAdminGeral, deletedQuery, deletedPage, fetchDeletedUsers]);

  // Auto-refresh 30s
  useEffect(() => {
    if (activeTab !== 'usuarios') return;
    const t = setInterval(() => fetchUsers(searchQuery, page), 30000);
    return () => clearInterval(t);
  }, [activeTab, fetchUsers, searchQuery, page]);

  // Chaves de licença
  useEffect(() => {
    if (activeTab !== 'licencas') return;
    const fetchKeys = async () => {
      const targetId = isAdminGeral
        ? safeString(selectedClientForKeys, '')
        : safeString(allClients.find(c => c.nome_empresa === licenseInfo.clientName)?.id, '');

      if (targetId) {
        try {
          const { data } = await api.get(API_KEYS_BY_CLIENT(targetId));
          const arr = Array.isArray(data) ? data : (data?.items || data?.data || []);
          setKeysOverview(Array.isArray(arr) ? arr : []);
        } catch { setKeysOverview([]); }
      } else {
        setKeysOverview([]);
      }
    };
    fetchKeys();
  }, [activeTab, selectedClientForKeys, isAdminGeral, licenseInfo.clientName, allClients]);

  /* ------------------------------------------------------------------
   * Seleção
   * ------------------------------------------------------------------ */
  const protectedIds = useMemo(
    () => new Set(usuarios.filter(u => u.perfil === 'admin_geral' || u.email === 'admin@guinchooliveira.com').map(u => u.id)),
    [usuarios],
  );
  const selectableIds = useMemo(
    () => usuarios.map(u => u.id).filter(id => !protectedIds.has(id)),
    [usuarios, protectedIds],
  );

  const toggleSelectOne = (id) => {
    if (protectedIds.has(id)) return;
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (selectableIds.length === 0) return;
    const allSelected = selectableIds.every(id => selectedUsers.includes(id));
    setSelectedUsers(allSelected ? [] : [...new Set([...selectedUsers, ...selectableIds])]);
  };
  const clearSelection = () => setSelectedUsers([]);

  /* ------------------------------------------------------------------
   * Confirm helpers
   * ------------------------------------------------------------------ */
  const openConfirm = (cfg) => setConfirmState({
    open: true,
    title:       cfg.title       || 'Confirmar',
    message:     cfg.message     || '',
    confirmText: cfg.confirmText || 'Confirmar',
    cancelText:  cfg.cancelText  || 'Cancelar',
    variant:     cfg.variant     || 'danger',
    loading:     false,
    onConfirm:   cfg.onConfirm   || null,
  });

  const closeConfirm      = () => setConfirmState(prev => ({ ...prev, open: false, loading: false, onConfirm: null }));
  const setConfirmLoading = (v) => setConfirmState(prev => ({ ...prev, loading: v }));

  /* ------------------------------------------------------------------
   * Ações em massa
   * ------------------------------------------------------------------ */
  const runBulkAction = async (action) => {
    if (selectedUsers.length === 0) return toast.warn('Selecione os registros primeiro.');
    const count = selectedUsers.length;
    let msg = `Confirmar ação para ${count} usuário(s)?`;
    if (action === 'force_logout') msg = `Derrubar sessão de ${count} usuário(s)?`;
    if (action === 'delete')       msg = `Enviar ${count} usuário(s) para "Usuários Excluídos"?`;

    openConfirm({
      title: 'Ação em massa', message: msg, confirmText: 'Confirmar',
      variant: action === 'delete' ? 'danger' : 'info',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await api.put(API_BULK_ACTIONS, { userIds: selectedUsers, action });
          toast.success('Operação em massa concluída!');
          setSelectedUsers([]);
          closeConfirm();
          fetchUsers(searchQuery, page);
        } catch (error) {
          toast.error(error?.response?.data?.error || 'Falha ao processar ação em massa.');
          setConfirmLoading(false);
        }
      },
    });
  };

  const handleForceLogoutBulk = () => runBulkAction('force_logout');
  const handleBlockBulk       = () => runBulkAction('block');
  const handleUnblockBulk     = () => runBulkAction('unblock');
  const handleDeleteBulk      = () => runBulkAction('delete');

  const handleMfaBulkAction = async (provider) => {
    const action = provider === 'disable' ? 'disable_mfa' : `enable_mfa_${provider}`;
    setMfaBulkModalOpen(false);
    openConfirm({
      title: 'Ação de MFA em Massa',
      message: provider === 'disable'
        ? `Desativar MFA de ${selectedUsers.length} usuário(s)?`
        : `Ativar MFA via ${provider.toUpperCase()} para ${selectedUsers.length} usuário(s)?`,
      confirmText: 'Confirmar',
      variant: provider === 'disable' ? 'danger' : 'info',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await api.put(API_BULK_ACTIONS, { userIds: selectedUsers, action });
          toast.success('MFA atualizado com sucesso!');
          setSelectedUsers([]);
          closeConfirm();
          fetchUsers(searchQuery, page);
        } catch {
          toast.error('Falha ao processar MFA em massa.');
          setConfirmLoading(false);
        }
      },
    });
  };

  /* ------------------------------------------------------------------
   * Ações individuais
   * ------------------------------------------------------------------ */
  const handleAllocateLicense = async (usuario_id, chave_id) => {
    try {
      await api.put(API_LICENSING_ALLOCATE, { usuario_id, chave_id });
      toast.success('Licença vinculada ao colaborador!');
      fetchUsers(searchQuery, page);
    } catch (error) { toast.error(error?.response?.data?.error || 'Erro na vinculação.'); }
  };

  const handleDeallocateLicense = async (chave_id) => {
    openConfirm({
      title: 'Liberar vaga', message: 'Desvincular esta licença? O slot voltará a ficar disponível.',
      confirmText: 'Liberar', variant: 'info',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await api.put(API_LICENSING_DEALLOCATE, { chave_id });
          toast.info('Licença liberada.');
          closeConfirm();
          fetchUsers(searchQuery, page);
        } catch (error) { toast.error(error?.response?.data?.error || 'Erro ao liberar.'); setConfirmLoading(false); }
      },
    });
  };

  const handleToggleBlock = async (usuario) => {
    if (usuario.perfil === 'admin_geral' || usuario.email === 'admin@guinchooliveira.com') {
      return toast.error('Não é possível bloquear o Administrador Geral.');
    }
    const novoStatus = usuario.status === 'ativo' ? 'bloqueado' : 'ativo';
    openConfirm({
      title: 'Alterar status', message: `Mudar status para "${novoStatus}"?`,
      confirmText: novoStatus === 'bloqueado' ? 'Bloquear' : 'Desbloquear', variant: 'info',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await api.put(`/api/usuarios/${usuario.id}/status`, { status: novoStatus });
          toast.info(`Status alterado: ${novoStatus}`);
          closeConfirm();
          fetchUsers(searchQuery, page);
        } catch (error) { toast.error(error?.response?.data?.error || 'Erro na alteração.'); setConfirmLoading(false); }
      },
    });
  };

  const handleDelete = async (id) => {
    openConfirm({
      title: 'Excluir usuário', message: 'Confirmar exclusão? O usuário será movido para a guia "Usuários Excluídos".',
      confirmText: 'Excluir', variant: 'danger',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await api.delete(`/api/usuarios/${id}`);
          toast.success('Usuário enviado para "Usuários Excluídos"!');
          closeConfirm();
          fetchUsers(searchQuery, page);
          if (isAdminGeral && activeTab === 'excluidos') fetchDeletedUsers(deletedQuery, deletedPage);
        } catch (error) { toast.error(error?.response?.data?.error || 'Erro na exclusão.'); setConfirmLoading(false); }
      },
    });
  };

  const handleRestoreDeleted = async (user) => {
    openConfirm({
      title: 'Restaurar usuário', message: `Restaurar "${safeString(user?.nome, 'Usuário')}" para o diretório ativo?`,
      confirmText: 'Restaurar', variant: 'info',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await api.put(API_RESTORE_USER(user.id));
          toast.success('Usuário restaurado com sucesso!');
          closeConfirm();
          fetchDeletedUsers(deletedQuery, deletedPage);
        } catch (e) { toast.error(e?.response?.data?.error || 'Falha ao restaurar.'); setConfirmLoading(false); }
      },
    });
  };

  const handlePurgeDeleted = async (user) => {
    openConfirm({
      title: 'Exclusão permanente',
      message: `Excluir permanentemente "${safeString(user?.nome, 'Usuário')}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir permanentemente', variant: 'danger',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await api.delete(API_PURGE_USER(user.id));
          toast.success('Usuário excluído permanentemente.');
          closeConfirm();
          fetchDeletedUsers(deletedQuery, deletedPage);
        } catch (e) { toast.error(e?.response?.data?.error || 'Falha ao excluir permanentemente.'); setConfirmLoading(false); }
      },
    });
  };

  const handleRequestLicense = async (requestedAmount, justification) => {
    try {
      const payload = { licencas_solicitadas: requestedAmount, justificativa: justification };
      if (isAdminGeral) {
        if (!selectedClientForKeys) { toast.warn('Selecione um cliente antes de solicitar.'); return; }
        payload.cliente_id = selectedClientForKeys;
      }
      await api.post('/api/licensing/request', payload);
      toast.success('Solicitação de licenças enviada com sucesso!');
      setIsLicenseModalOpen(false);
    } catch (error) { toast.error(error?.response?.data?.error || 'Erro ao enviar a solicitação.'); }
  };

  const openGovernance = (user) => { setEditingUser(user); setIsEditModalOpen(true); };

  /* ------------------------------------------------------------------
   * Resumo de licenças
   * ------------------------------------------------------------------ */
  const selectedClientName = useMemo(() => {
    if (!isAdminGeral) return licenseInfo.clientName;
    const found = allClients.find(c => safeString(c.id) === safeString(selectedClientForKeys));
    return safeString(found?.nome_empresa, '');
  }, [isAdminGeral, licenseInfo.clientName, allClients, selectedClientForKeys]);

  const licenseSummary = useMemo(() => {
    if (isAdmin) {
      return { used: safeInt(licenseInfo.used, 0), total: safeInt(licenseInfo.total, 0), clientName: safeString(licenseInfo.clientName, '') };
    }
    if (isAdminGeral) {
      const total = Array.isArray(keysOverview) ? keysOverview.length : 0;
      const used  = Array.isArray(keysOverview) ? keysOverview.filter(k => k?.usuario_id_alocado).length : 0;
      return { used, total, clientName: selectedClientName };
    }
    return { used: 0, total: 0, clientName: '' };
  }, [isAdmin, licenseInfo, isAdminGeral, keysOverview, selectedClientName]);

  const canRequestUpgrade =
    (isAdmin && licenseSummary.clientName) ||
    (isAdminGeral && safeString(selectedClientForKeys) !== '');

  /* ------------------------------------------------------------------
   * Tenant filter label (nome do tenant selecionado ou "Todos")
   * ------------------------------------------------------------------ */
  const tenantFilterLabel = useMemo(() => {
    if (!selectedTenantFilter) return null;
    return clientMap.get(selectedTenantFilter) || selectedTenantFilter;
  }, [selectedTenantFilter, clientMap]);

  /* ------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------ */
  return (
    <div className="usuarios-main-wrapper">
      <header className="main-view-header">
        <div className="view-title-box">
          <button onClick={() => navigate(-1)} className="btn-back" title="Voltar"><FaArrowLeft /></button>
          <div
            className="title-leading-icon"
            aria-hidden="true"
            style={{
              width: 44, height: 44, borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#ffffff', boxShadow: '0 6px 16px rgba(15,23,42,0.08)', border: '1px solid #e2e8f0',
            }}
          >
            <FaIdCard size={18} color="#101C5D" />
          </div>
          <h1 style={{ marginLeft: 8 }}>Diretório de Usuários &amp; Governança</h1>
        </div>

        {/* ── Ações: apenas "Cadastrar Colaborador" — APIs e Docs foram removidos ── */}
        <div className="view-actions-box">
          {canWrite && (
            <button onClick={() => setIsAddModalOpen(true)} className="btn-ultra primary">
              <FaUserPlus /> Cadastrar Colaborador
            </button>
          )}
        </div>
      </header>

      <div className="view-content-card">
        <nav className="view-tabs">
          <button className={activeTab === 'usuarios' ? 'active' : ''} onClick={() => setActiveTab('usuarios')}>
            Lista de Usuários ({totalUsers || usuarios.length})
          </button>
          {canWrite && (
            <button className={activeTab === 'licencas' ? 'active' : ''} onClick={() => setActiveTab('licencas')}>
              Gerenciamento de Vagas
            </button>
          )}
          {isAdminGeral && (
            <button className={activeTab === 'excluidos' ? 'active' : ''} onClick={() => setActiveTab('excluidos')}>
              Usuários Excluídos ({deletedTotal || deletedUsers.length})
            </button>
          )}
        </nav>

        <div className="tab-pane">
          {/* ── TAB: Usuários ── */}
          {activeTab === 'usuarios' && (
            <div className="directory-view fade-in">
              <BulkActionsBar
                users={usuarios}
                selectedIds={selectedUsers}
                onForceLogout={handleForceLogoutBulk}
                onBlock={handleBlockBulk}
                onUnblock={handleUnblockBulk}
                onDelete={handleDeleteBulk}
                onClear={clearSelection}
                onMfaAction={() => setMfaBulkModalOpen(true)}
              />

              <div className="directory-filters">
                {/* Busca textual */}
                <div className="search-wrapper">
                  <input
                    type="text"
                    placeholder="Buscar por nome, CPF ou e-mail..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(sanitizeQuery(e.target.value))}
                  />
                </div>

                {/* ── Filtro de tenant — exclusivo para admin_geral ── */}
                {isAdminGeral && (
                  <div className="tenant-filter-wrapper">
                    <FaFilter className="tenant-filter-icon" size={12} />
                    <select
                      value={selectedTenantFilter}
                      onChange={e => setSelectedTenantFilter(e.target.value)}
                      aria-label="Filtrar por tenant"
                    >
                      <option value="">Todos os tenants</option>
                      {allClients.map(c => (
                        <option key={safeString(c.id)} value={safeString(c.id)}>
                          {safeString(c.nome_empresa, 'Cliente')}
                        </option>
                      ))}
                    </select>
                    {/* Badge de contexto quando filtro está ativo */}
                    {tenantFilterLabel && (
                      <button
                        className="tenant-filter-clear"
                        onClick={() => setSelectedTenantFilter('')}
                        title="Limpar filtro de tenant"
                        aria-label="Limpar filtro de tenant"
                      >
                        <FaTimes size={10} />
                      </button>
                    )}
                  </div>
                )}

                <div className="filters-right">
                  {selectedUsers.length > 0 && (
                    <span className="selected-badge">{selectedUsers.length} selecionado(s)</span>
                  )}
                </div>
              </div>

              {/* Banner informativo quando filtrando por tenant */}
              {isAdminGeral && tenantFilterLabel && (
                <div className="tenant-filter-banner">
                  <FaBuilding size={13} />
                  <span>Exibindo usuários do tenant <strong>{tenantFilterLabel}</strong></span>
                  <button onClick={() => setSelectedTenantFilter('')} className="tenant-banner-clear">
                    <FaTimes size={10} /> Ver todos
                  </button>
                </div>
              )}

              <UsersTable
                users={usuarios}
                now={now}
                selectedIds={selectedUsers}
                toggleSelectOne={toggleSelectOne}
                toggleSelectAll={toggleSelectAll}
                canWrite={canWrite}
                perfilLogado={perfilKey}
                onGovern={openGovernance}
                onToggleBlock={handleToggleBlock}
                onDelete={handleDelete}
                onAudit={(u) => setHistoryUserId(u.id)}
                clientMap={clientMap}
                showTenant={showTenantBadge}
              />

              <Pagination page={page} totalPages={totalPages} onPage={setPage} />
            </div>
          )}

          {/* ── TAB: Licenças ── */}
          {activeTab === 'licencas' && (
            <div className="licensing-view fade-in">
              {isAdminGeral && (
                <div className="admin-client-picker">
                  <label>Visualizar licenças de:</label>
                  <select value={selectedClientForKeys} onChange={(e) => setSelectedClientForKeys(safeString(e.target.value, ''))}>
                    <option value="">Selecione o Cliente...</option>
                    {allClients.map(c => <option key={safeString(c.id)} value={safeString(c.id)}>{safeString(c.nome_empresa, 'Cliente')}</option>)}
                  </select>
                </div>
              )}

              {canWrite && (
                <div className={`license-summary-card ${licenseSummary.total > 0 && licenseSummary.used >= licenseSummary.total ? 'alert' : ''}`}>
                  <div className="license-stats">
                    <h4>{safeString(licenseSummary.clientName || (isAdminGeral ? '(Selecione um cliente)' : ''), '-')}</h4>
                    <p>Utilização: <strong>{safeInt(licenseSummary.used, 0)} / {safeInt(licenseSummary.total, 0)}</strong> slots</p>
                  </div>
                  <button
                    className="btn-request-more"
                    onClick={() => setIsLicenseModalOpen(true)}
                    disabled={!canRequestUpgrade}
                    title={canRequestUpgrade ? 'Solicitar Upgrade' : (isAdminGeral ? 'Selecione um cliente para solicitar' : 'Dados de licenças indisponíveis')}
                  >
                    Solicitar Upgrade
                  </button>
                </div>
              )}

              <LicenseManagementTab
                keys={Array.isArray(keysOverview) ? keysOverview : []}
                users={isAdmin ? usuarios : usuarios.filter(u => safeString(u.cliente_id) === safeString(selectedClientForKeys))}
                onAllocate={handleAllocateLicense}
                onDeallocate={handleDeallocateLicense}
              />
            </div>
          )}

          {/* ── TAB: Excluídos ── */}
          {activeTab === 'excluidos' && isAdminGeral && (
            <div className="directory-view fade-in">
              <div className="directory-filters">
                <div className="search-wrapper">
                  <input
                    type="text"
                    placeholder="Buscar excluídos por nome ou e-mail..."
                    value={deletedQuery}
                    onChange={(e) => setDeletedQuery(sanitizeQuery(e.target.value))}
                  />
                </div>
              </div>

              <ul className="user-list user-list--trash">
                <li className="list-header-row">
                  <div className="cell-check"><span /></div>
                  <div className="cell-main">Usuário / Email</div>
                  <div className="cell-status">Ações</div>
                  <div className="cell-actions">Operações</div>
                </li>
                {deletedUsers.length === 0 ? (
                  <div className="empty-state">
                    <h4>Nenhum usuário excluído</h4>
                    <p>Quando você excluir um usuário, ele aparecerá aqui para restauração ou purge.</p>
                  </div>
                ) : deletedUsers.map(u => (
                  <li key={u.id} className="user-row-item is-blocked">
                    <div className="cell-check"><FaTrash title="Excluído" /></div>
                    <div className="cell-main">
                      <div className="avatar-wrapper">
                        <img src={safeAvatarUrl(u.nome, u.foto_perfil)} alt={`Avatar de ${u.nome}`} />
                      </div>
                      <div className="user-meta">
                        <strong>{u.nome}</strong>
                        <span>{u.email}</span>
                        {u.cliente_id && clientMap.has(u.cliente_id) && (
                          <span className="tag-tenant">
                            <FaBuilding size={9} />{clientMap.get(u.cliente_id)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="cell-status">
                      <span className="tag-native" title="Registro excluído">Excluído</span>
                    </div>
                    <div className="cell-actions" style={{ display: 'flex', gap: 8 }}>
                      <button className="action-btn edit"   onClick={() => handleRestoreDeleted(u)} title="Restaurar"><FaUserPlus /> Restaurar</button>
                      <button className="action-btn delete" onClick={() => handlePurgeDeleted(u)}   title="Excluir permanentemente"><FaTrash /> Purge</button>
                    </div>
                  </li>
                ))}
              </ul>
              <Pagination page={deletedPage} totalPages={deletedTotalPages} onPage={setDeletedPage} />
            </div>
          )}
        </div>
      </div>

      {/* ── Modal: MFA em Massa ── */}
      {mfaBulkModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content modal-content--simple">
            <header className="modal-header-simple">
              <h3>Habilitar MFA em Massa</h3>
              <button onClick={() => setMfaBulkModalOpen(false)} className="modal-close-button"><FaTimes /></button>
            </header>
            <div className="modal-body-simple" style={{ textAlign: 'center' }}>
              <p>Selecione o provedor para <strong>{selectedUsers.length}</strong> usuário(s):</p>
              <div style={{ display: 'flex', gap: 15, justifyContent: 'center', marginTop: 20 }}>
                <button className="btn-ultra dark" onClick={() => handleMfaBulkAction('google')}>
                  <FaGoogle /> Google
                </button>
                <button className="btn-ultra dark" onClick={() => handleMfaBulkAction('microsoft')}>
                  <FaMicrosoft /> Microsoft
                </button>
              </div>
              <hr style={{ margin: '20px 0', opacity: 0.2 }} />
              <button className="btn-ultra secondary" onClick={() => handleMfaBulkAction('disable')}>
                Desativar MFA de todos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Novo usuário ── */}
      {isAddModalOpen && (
        <NovosUsuarios
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => fetchUsers(searchQuery, page)}
          perfilLogado={perfilKey}
          allClients={allClients}
          usuarios={usuarios}
          limitReached={limitReached}
          setIsLicenseModalOpen={setIsLicenseModalOpen}
          licenseInfo={licenseInfo}
        />
      )}

      {/* ── Modal: Governança ── */}
      {isEditModalOpen && editingUser && (
        isAdminGeral ? (
          <EditModalAdminGeral
            usuarioId={editingUser.id}
            onClose={() => { setIsEditModalOpen(false); setEditingUser(null); }}
            onUpdate={() => fetchUsers(searchQuery, page)}
            allClients={allClients}
          />
        ) : (
          <SimpleEditModal
            usuario={editingUser}
            onClose={() => { setIsEditModalOpen(false); setEditingUser(null); }}
            onUpdate={() => fetchUsers(searchQuery, page)}
          />
        )
      )}

      {/* ── Modal: Solicitar licenças ── */}
      {isLicenseModalOpen && (
        <LicenseRequestModal
          currentLicenses={licenseSummary.total}
          clientName={licenseSummary.clientName}
          onClose={() => setIsLicenseModalOpen(false)}
          onSubmit={handleRequestLicense}
        />
      )}

      {/* ── Modal: Confirm ── */}
      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        loading={confirmState.loading}
        onConfirm={confirmState.onConfirm}
        onClose={closeConfirm}
      />

      {/* ── Modal: Histórico + PostHog ── */}
      {historyUserId && (
        <UserHistoryModal
          usuarioId={historyUserId}
          onClose={() => setHistoryUserId(null)}
          perfilLogado={perfilKey}
        />
      )}
    </div>
  );
}